'use client';
/**
 * L'ARBRE D'ORGANIGRAMME — composant partagé (Lot 1, 03/09/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── Le problème qu'il règle ────────────────────────────────────────────────
 *
 * Le tracé maison empile des `flex` récursifs : chaque fratrie s'aligne sur la
 * hauteur de sa plus grande carte. Or la carte du Dr Hmidet fait environ QUATRE
 * FOIS celle d'un « Préleveur » — elle liste ses pilotages et ses suppléances —
 * et ce sont donc quatre hauteurs de carte réservées sur toute la rangée, pour
 * une seule qui en a besoin.
 *
 * `d3-flextree`, que `d3-org-chart` embarque, résout exactement ce cas : il
 * produit un arbre SERRÉ même quand les nœuds ont des hauteurs très
 * différentes. C'est l'algorithme qu'on vient chercher ; le reste — repli,
 * zoom, ajustement, recherche — vient avec.
 *
 * ── La contrainte d'intégration, et le choix fait ──────────────────────────
 *
 * `nodeContent` doit rendre une CHAÎNE HTML, injectée dans un `foreignObject`
 * SVG. Les cartes ne peuvent donc pas rester des composants React montés.
 *
 * Choix retenu (spec §II, option 1) : `renderToStaticMarkup` sur le composant
 * de carte EXISTANT. Le design n'est pas dupliqué — c'est la seule façon
 * d'éviter qu'il diverge entre l'arbre et le reste de l'application.
 *
 * ⚠️ CONSÉQUENCE À NE PAS OUBLIER : **aucun gestionnaire d'événement React ne
 * survit** au passage par la chaîne. Les clics sont donc repris par DÉLÉGATION
 * sur le conteneur, en lisant un `data-carte` posé sur la carte. Toute la
 * couche est isolée ici, dans ce fichier, et nulle part ailleurs.
 *
 * ⚠️ CHARGEMENT AU MONTAGE SEULEMENT. `d3-org-chart` touche le DOM dès son
 * import : le charger au module le ferait exécuter au rendu serveur de Next, où
 * `document` n'existe pas. L'import est donc dynamique, dans l'effet.
 */
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

export interface NoeudArbre {
  id: string;
  /** `null` = racine. Plusieurs racines sont acceptées. */
  parentId: string | null;
}

export interface PoigneesArbre {
  ajuster: () => void;
  zoomer: (sens: 1 | -1) => void;
  toutDeplier: () => void;
  toutReplier: () => void;
  centrer: (id: string) => void;
}

export interface ProprietesArbre<T extends NoeudArbre> {
  donnees: T[];
  /** Le gabarit d'une carte. Rendu en HTML statique : pas de gestionnaires. */
  carte: (n: T) => React.ReactElement;
  /** Hauteur estimée d'une carte, en pixels. `d3-org-chart` l'exige AVANT le
   *  rendu — il ne peut pas la mesurer. Une estimation basse fait chevaucher,
   *  une estimation haute gaspille : on la calcule depuis le contenu. */
  hauteur: (n: T) => number;
  largeur: (n: T) => number;
  /** `top` = les cartes empilées vers le bas ; `left` = de gauche à droite. */
  sens: 'top' | 'left';
  /** Le layout serré de `d3-flextree` — la raison d'être de ce composant. */
  compact: boolean;
  /** Identifiants à mettre en évidence (résultat de recherche). */
  surlignes?: string[];
  /** Nom de l'entité, posé en racine quand l'organigramme en a plusieurs. */
  nomEntite?: string;
  /** `zone` : la valeur du `data-zone` le plus proche DANS la carte, quand le
   *  clic est tombé sur une partie qui a son propre sens (la pastille du compte,
   *  03/10/2026). Absent = un clic sur la carte elle-même. */
  onCarteClic?: (id: string, zone?: string) => void;
  /** Profondeur au-delà de laquelle l'arbre s'ouvre replié. `0` = tout ouvert. */
  replierAuDela?: number;
  /** ⚠️ UN RAPPEL, PAS UNE RÉFÉRENCE MUTABLE. Écrire dans la `ref` d'un parent
   *  depuis un effet est ce que le compilateur React refuse — et il a raison :
   *  rien ne garantit que le parent se re-rende pour en tenir compte. Le rappel
   *  le prévient, il décide. */
  onPoignees?: (p: PoigneesArbre) => void;
  hauteurVue?: number;
}

export function ArbreOrganigramme<T extends NoeudArbre>({
  donnees, carte, hauteur, largeur, sens, compact,
  surlignes, onCarteClic, replierAuDela = 0, onPoignees, hauteurVue = 620,
  nomEntite = 'Laboratoire',
}: ProprietesArbre<T>) {
  const boite = React.useRef<HTMLDivElement | null>(null);
  const graphe = React.useRef<import('d3-org-chart').OrgChart<T> | null>(null);
  const [pret, setPret] = React.useState(false);
  const [erreur, setErreur] = React.useState('');

  /* Les propriétés changent à chaque rendu ; le graphe, lui, vit entre les
     rendus. On garde donc la dernière version dans une référence, que les
     rappels du graphe consultent — sinon ils capturent la première et
     l'organigramme cesse de suivre les données au bout d'un changement. */
  const dernieres = React.useRef({ donnees, carte, hauteur, largeur, sens, compact, surlignes, onCarteClic });
  React.useEffect(() => {
    dernieres.current = { donnees, carte, hauteur, largeur, sens, compact, surlignes, onCarteClic };
  });

  /* ── La DÉLÉGATION D'ÉVÉNEMENTS, isolée ici ─────────────────────────────
     Une seule écoute sur le conteneur, qui remonte au plus proche `data-carte`.
     Poser un écouteur par carte reviendrait à en poser dix-huit à chaque
     re-rendu du graphe, et à les fuir tous. */
  React.useEffect(() => {
    const el = boite.current;
    if (!el) return undefined;
    const clic = (e: MouseEvent) => {
      const cible = (e.target as HTMLElement)?.closest?.('[data-carte]') as HTMLElement | null;
      const id = cible?.getAttribute('data-carte');
      // La zone n'est cherchée qu'À L'INTÉRIEUR de la carte : un `data-zone`
      // d'un ancêtre du graphe ne doit pas teinter tous les clics.
      const zone = (e.target as HTMLElement)?.closest?.('[data-zone]') as HTMLElement | null;
      if (id) dernieres.current.onCarteClic?.(id, zone && cible?.contains(zone) ? zone.getAttribute('data-zone') || undefined : undefined);
    };
    el.addEventListener('click', clic);
    return () => el.removeEventListener('click', clic);
  }, []);

  React.useEffect(() => {
    let vivant = true;
    void (async () => {
      try {
        const { OrgChart } = await import('d3-org-chart');
        if (!vivant || !boite.current) return;
        graphe.current = new OrgChart<T>();
        setPret(true);
      } catch (e) {
        /* ⚠️ UN ÉCHEC DE CHARGEMENT SE DIT. Sans message, l'écran resterait
           blanc et l'on croirait l'organigramme vide — c'est-à-dire qu'aucune
           organisation n'est déclarée. */
        if (vivant) setErreur(e instanceof Error ? e.message : 'Le tracé n’a pas pu être chargé.');
      }
    })();
    return () => { vivant = false; };
  }, []);

  React.useEffect(() => {
    const g = graphe.current;
    if (!pret || !g || !boite.current) return;
    if (!donnees.length) return;

    const surligne = new Set(surlignes || []);

    /* ── UNE SEULE RACINE, PARCE QUE LA BIBLIOTHÈQUE L'EXIGE ───────────────
     * `d3-org-chart` lève « multiple roots » dès qu'il en trouve deux. Or cet
     * organigramme en a deux, et c'est JUSTE : la co-direction est portée par
     * Ahmed Hmidet et Moncef Harzallah, à égalité. Le tracé maison les
     * dessinait côte à côte sans rien au-dessus.
     *
     * On ajoute donc le nœud que le document sous-entend déjà : le laboratoire
     * lui-même. Ce n'est pas un artifice technique — un organigramme dont deux
     * personnes sont à la racine se lit « elles répondent de l'entité », et
     * c'est exactement ce que la case rend visible. La nommer vaut mieux que de
     * la masquer : une racine invisible laisserait un trait partir de nulle part.
     *
     * ⚠️ Elle n'apparaît QUE s'il y a réellement plusieurs racines. Avec une
     * seule, on n'ajoute rien — inventer un niveau là où il n'en manque pas
     * ferait descendre tout l'organigramme d'un cran. */
    const idsConnus = new Set(donnees.map((n) => n.id));
    const racines = donnees.filter((n) => !n.parentId || !idsConnus.has(n.parentId));
    const RACINE = '__entite__';
    const rangees = racines.length > 1
      ? [
        { id: RACINE, parentId: null } as unknown as T,
        ...donnees.map((n) => (racines.includes(n) ? { ...n, parentId: RACINE } : n)),
      ]
      : donnees;

    g.container(boite.current)
      .data(rangees)
      .svgHeight(hauteurVue)
      .compact(compact)
      .layout(sens)
      .nodeWidth((d) => dernieres.current.largeur(d.data))
      .nodeHeight((d) => (d.data.id === RACINE ? 44 : dernieres.current.hauteur(d.data)))
      .childrenMargin(() => 44)
      .compactMarginBetween(() => 14)
      .compactMarginPair(() => 22)
      .siblingsMargin(() => 16)
      .neighbourMargin(() => 16)
      .nodeContent((d) => {
        if (d.data.id === RACINE) {
          /* La case de l'entité : sobre, sans commande, et sans `data-carte` —
             elle ne mène à aucun dossier. */
          return '<div style="display:flex;align-items:center;justify-content:center;height:100%;'
            + 'border:1px dashed var(--border-strong);border-radius:8px;background:var(--gray-25);'
            + 'font-size:12.5px;font-weight:700;color:var(--text-muted);box-sizing:border-box;">'
            + `${nomEntite}</div>`;
        }
        const html = renderToStaticMarkup(dernieres.current.carte(d.data));
        /* `data-carte` porte l'identifiant : c'est par lui que la délégation
           retrouve qui a été cliqué, puisque aucun gestionnaire n'a survécu. */
        return `<div data-carte="${String(d.data.id).replace(/"/g, '&quot;')}" style="width:100%;height:100%;`
          + `outline:${surligne.has(d.data.id) ? '2px solid var(--amber-500)' : 'none'};outline-offset:2px;`
          + 'cursor:pointer;box-sizing:border-box;">' + html + '</div>';
      })
      .render();

    /* ⚠️ L'ARBRE S'OUVRE ENTIER PAR DÉFAUT. `d3-org-chart` ne montre que la
       racine et son premier niveau : mesuré, deux cartes sur dix-neuf. Sur un
       organigramme — un document qu'on ouvre POUR voir l'ensemble — c'est le
       contraire de ce qu'on vient y chercher, et rien à l'écran ne dit que
       dix-sept cartes sont repliées.
       `replierAuDela` reste disponible pour le jour où l'effectif rendra le
       dépli complet illisible ; il ne sert pas encore. */
    if (!replierAuDela) {
      try { g.expandAll(); } catch { /* la version peut ne pas l'exposer */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pret, donnees, sens, compact, surlignes, hauteurVue, replierAuDela, nomEntite]);

  React.useEffect(() => {
    if (!pret || !onPoignees) return;
    onPoignees({
      ajuster: () => { try { graphe.current?.fit(); } catch { /* pas encore rendu */ } },
      zoomer: (s) => { try { if (s > 0) graphe.current?.zoomIn(); else graphe.current?.zoomOut(); } catch { /* idem */ } },
      toutDeplier: () => { try { graphe.current?.expandAll(); } catch { /* idem */ } },
      toutReplier: () => { try { graphe.current?.collapseAll(); } catch { /* idem */ } },
      centrer: (id) => { try { graphe.current?.setCentered(id).render(); } catch { /* idem */ } },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pret]);

  if (erreur) {
    return (
      <div role="alert" style={{ padding: 16, fontSize: 12.5, color: 'var(--red-600)', lineHeight: 1.55 }}>
        <strong>Le tracé de l’organigramme n’a pas pu être chargé.</strong>
        <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>{erreur}</div>
      </div>
    );
  }
  return <div ref={boite} className="arbre-organigramme" style={{ width: '100%', minHeight: 200 }} />;
}
